import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import { brand } from '../../theme';
import BrandMark from '../ui/BrandMark';
import BetaBadge from '../ui/BetaBadge';
import { AUTH_TITLE_VARIANT } from './AuthStatus';

interface AuthLayoutProps {
  children: React.ReactNode;
  /** The page heading. Leave it out when the content brings its own (an AuthStatus). */
  title?: string;
  subtitle?: React.ReactNode;
}

const highlights = [
  {
    icon: <VideoLibraryOutlinedIcon />,
    title: 'One organized video workspace',
    description: 'Keep source files, project context, and revisions together.',
  },
  {
    icon: <EditNoteOutlinedIcon />,
    title: 'Frame-aware feedback',
    description: 'Turn exact moments into clear, actionable editing direction.',
  },
  {
    icon: <Groups2OutlinedIcon />,
    title: 'A cleaner creator-editor handoff',
    description: 'Invite collaborators and keep every decision tied to the project.',
  },
];

/**
 * The one shell for signing in and onboarding: sign in, sign up, password
 * reset, email verification, editor invitation and registration, sign out.
 * A flat ink brand panel beside a white working column; on phones the panel
 * shrinks to a bar with the logo.
 */
const AuthLayout: React.FC<AuthLayoutProps> = ({ children, title, subtitle }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: { xs: 'stretch', md: 'center' },
      backgroundColor: 'background.default',
      px: { xs: 0, md: 3 },
      py: { xs: 0, md: 4 },
    }}
  >
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 1200,
        mx: 'auto',
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.05fr) minmax(0, 0.95fr)' },
        // The brand bar hugs its content on phones; the column below takes the rest.
        gridTemplateRows: { xs: 'auto 1fr', md: 'auto' },
        minHeight: { xs: '100vh', md: 680 },
        overflow: 'hidden',
        border: { xs: 0, md: `1px solid ${brand.border}` },
        borderRadius: { xs: 0, md: 3 },
        boxShadow: 'none',
      }}
    >
      <Box
        component="section"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 6,
          p: { xs: 2, sm: 3, md: 6 },
          color: brand.ivory,
          backgroundColor: brand.ink,
        }}
      >
        <Box
          component="a"
          href="https://pithplay.com"
          aria-label="PithPlay home"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: 1.25,
            color: 'inherit',
            textDecoration: 'none',
            borderRadius: 1,
            '&:focus-visible': { outline: `2px solid ${brand.ivory}`, outlineOffset: 4 },
          }}
        >
          <BrandMark size={36} onDark />
          <Typography
            component="span"
            sx={{ color: 'inherit', fontSize: '1.4rem', fontWeight: 850, letterSpacing: '-0.04em', lineHeight: 1 }}
          >
            PithPlay
          </Typography>
          <BetaBadge onDark />
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Typography component="h2" variant="h1" sx={{ color: brand.ivory, maxWidth: 500, mb: 2.5 }}>
            Better feedback. Better edits. Fewer revision loops.
          </Typography>
          <Typography sx={{ color: alpha(brand.ivory, 0.78), maxWidth: 480, fontSize: '1.02rem', lineHeight: 1.7 }}>
            Give editors the files, moments, and project context they need, without scattering work across email
            and chat.
          </Typography>

          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 6, display: 'grid', gap: 3 }}>
            {highlights.map((item) => (
              <Box component="li" key={item.title} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    color: alpha(brand.ivory, 0.9),
                    backgroundColor: alpha(brand.ivory, 0.08),
                    border: `1px solid ${alpha(brand.ivory, 0.14)}`,
                    '& svg': { fontSize: 20 },
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography sx={{ color: brand.ivory, fontWeight: 700, mb: 0.25 }}>{item.title}</Typography>
                  <Typography variant="body2" sx={{ color: alpha(brand.ivory, 0.72), maxWidth: 390 }}>
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography variant="caption" sx={{ display: { xs: 'none', md: 'block' }, color: alpha(brand.ivory, 0.7) }}>
          Secure collaboration for creators, editors, and production teams.
        </Typography>
      </Box>

      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-start', md: 'center' },
          minWidth: 0,
          backgroundColor: 'background.paper',
          px: { xs: 3, sm: 6, md: 7 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 440, mx: 'auto' }}>
          {title && (
            <Box sx={{ mb: 4 }}>
              <Typography component="h1" variant={AUTH_TITLE_VARIANT} color="text.primary" sx={{ mb: subtitle ? 1 : 0 }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body1" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
          {children}
        </Box>
      </Box>
    </Paper>
  </Box>
);

export default AuthLayout;
